import type { components } from '@multaqa/api-client'
import {
  type UseMutationOptions,
  type UseQueryOptions,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query'
import { api } from '@/lib/api'

/**
 * The typed boundary to the company settings endpoints.
 *
 * All calls target the ACTIVE company (AC-01), so they carry the
 * `X-Company-Id` header like every other company-scoped call.
 *
 * ## The approval threshold wire format
 *
 * `CompanySettingUpdateRequest` documents ADR-0012 explicitly: the amount
 * arrives as a decimal STRING and is stored by the MoneyCast at full scale;
 * a JSON number is parsed into a double before validation and can genuinely
 * fail the `decimal:0,4` rule (a double like `0.00030000000000000003` has
 * more than four decimal places). The generated schema types the request
 * field as `number` — a Scramble inference of the `decimal` rule — so the
 * body is narrowed through the generated type at the boundary while the
 * wire keeps the documented string.
 */

export type CompanySettingResource = components['schemas']['CompanySettingResource']

const companyHeader = (companyId: string): { 'X-Company-Id': string } => ({
  'X-Company-Id': companyId,
})

export function useCompanySettings(
  companyId: string | null,
  options?: Omit<UseQueryOptions<CompanySettingResource, Error>, 'queryKey' | 'queryFn'>,
) {
  return useQuery<CompanySettingResource, Error>({
    queryKey: ['companies', companyId, 'settings'] as const,
    enabled: Boolean(companyId),
    ...options,
    queryFn: async (): Promise<CompanySettingResource> => {
      if (!companyId) {
        throw new Error('No active company')
      }
      const { data } = await api.GET('/companies/{company}/settings', {
        params: { path: { company: companyId } },
        headers: companyHeader(companyId),
      })
      if (!data) {
        throw new Error('GET /companies/{company}/settings returned no body')
      }
      return data.data
    },
  })
}

/**
 * A partial PATCH: each settings section sends only its own fields, so a save
 * in one section never overwrites another section with a stale value. A key
 * left `undefined` is dropped by `JSON.stringify` and the API leaves it alone.
 */
export interface UpdateCompanySettingsInput {
  default_payment_terms_days?: number
  allow_negative_stock?: boolean
  over_receipt_policy?: 'block' | 'allow'
  branding_accent_color?: string | null
  branding_logo_path?: string | null
  /** An IANA zone name; the API applies daylight saving time itself. */
  timezone?: string
  require_approval_above_amount?: string | null
  approval_threshold_currency?: string | null
  adjustment_reasons?: string[]
  /** Sent only when it changes: a change needs `costing_method.edit` and converts stock. */
  default_costing_method?: 'standard' | 'average' | 'fifo'
  /** Sent only while unset: the API sets the cutover once and refuses a second set. */
  costing_cutover_date?: string
}

/**
 * The saved logo's bytes, for the settings preview. The request needs the
 * `X-Company-Id` header, which an `<img src>` cannot send. The key holds the
 * stored path, which is new on every upload, so a new logo is never stale.
 */
export function useCompanyLogoPreview(companyId: string, logoPath: string | null | undefined) {
  return useQuery<Blob, Error>({
    // Outside the settings key on purpose: a settings save that clears the
    // path must not refetch the old logo into a 404.
    queryKey: ['companies', companyId, 'logo', logoPath] as const,
    enabled: Boolean(logoPath),
    retry: false,
    queryFn: async () => {
      const { data } = await api.GET('/companies/{company}/settings/logo', {
        params: { path: { company: companyId } },
        headers: companyHeader(companyId),
        parseAs: 'blob',
      })
      if (!data) throw new Error('GET /companies/{company}/settings/logo returned no body')
      return data
    },
  })
}

/**
 * Uploads a logo (a File) or removes it (`null`). The server checks the real
 * file type and size and answers a 422 whose message names both.
 */
export function useChangeCompanyLogo(
  companyId: string,
  options?: Omit<UseMutationOptions<CompanySettingResource, Error, File | null>, 'mutationFn'>,
) {
  const queryClient = useQueryClient()
  const userOnSuccess = options?.onSuccess
  return useMutation<CompanySettingResource, Error, File | null>({
    ...options,
    mutationFn: async (file) => {
      const params = { path: { company: companyId } }
      const headers = companyHeader(companyId)
      if (file === null) {
        const { data } = await api.DELETE('/companies/{company}/settings/logo', { params, headers })
        if (!data) throw new Error('DELETE /companies/{company}/settings/logo returned no body')
        return data.data
      }
      const form = new FormData()
      form.append('logo', file)
      // The generated schema types a binary field as a string; the runtime
      // value stays FormData, so the browser adds the multipart boundary.
      const { data } = await api.POST('/companies/{company}/settings/logo', {
        params,
        headers,
        body: form as unknown as { logo: string },
      })
      if (!data) throw new Error('POST /companies/{company}/settings/logo returned no body')
      return data.data
    },
    onSuccess: (data, variables, context) => {
      queryClient.setQueryData(['companies', companyId, 'settings'], data)
      if (userOnSuccess) {
        ;(userOnSuccess as (d: typeof data, v: typeof variables, c: typeof context) => void)(
          data,
          variables,
          context,
        )
      }
    },
  })
}

export function useUpdateCompanySettings(
  companyId: string,
  options?: Omit<
    UseMutationOptions<CompanySettingResource, Error, UpdateCompanySettingsInput>,
    'mutationFn'
  >,
) {
  const queryClient = useQueryClient()
  const userOnSuccess = options?.onSuccess
  return useMutation<CompanySettingResource, Error, UpdateCompanySettingsInput>({
    ...options,
    mutationFn: async (input) => {
      const { data } = await api.PATCH('/companies/{company}/settings', {
        params: { path: { company: companyId } },
        headers: companyHeader(companyId),
        body: input as unknown as components['schemas']['CompanySettingUpdateRequest'],
      })
      if (!data) {
        throw new Error('PATCH /companies/{company}/settings returned no body')
      }
      return data.data
    },
    onSuccess: (data, variables, context) => {
      void queryClient.invalidateQueries({
        queryKey: ['companies', companyId, 'settings'],
      })
      if (userOnSuccess) {
        ;(userOnSuccess as (d: typeof data, v: typeof variables, c: typeof context) => void)(
          data,
          variables,
          context,
        )
      }
    },
  })
}
